import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { CalendarMonthStartOption, CalendarPostSize, PlannedPostsLayout, initialState } from './state'
import { PostDestination, sortByKeyAscending } from 'shared'
import { createPostSuccess } from 'services/compose'
import { startImpersonation } from 'admin/services/actions'
import { Weekday } from 'utils/calendar'
import {
  createBucket,
  deleteBucket,
  getBuckets,
  getDefaultBuckets,
  getPostSlotsSuccess,
  getPostsCount,
  reorderBuckets,
  updateBucket,
  getFailedPostsCount,
  setSelectedProfiles,
  updateBucketGroup,
  deleteBucketGroup,
  getBucketById
} from './actions'
import { getDestinationsSuccess } from 'services/destinations'
import { setComposerProfiles, logoutAction } from 'services/shared/actions'

const postsSlice = createSlice({
  name: 'posts',
  initialState: initialState(),
  reducers: {
    setSelectedList: (state, action: PayloadAction<string | undefined>) => {
      state.ui.selectedListId = action.payload
    },
    setActiveSidebarView: (state, action: PayloadAction<'grid' | 'list' | 'network'>) => {
      state.ui.selectionActiveView = action.payload
    },
    toggleEmptySlotsVisible: (state) => {
      const next = !state.ui.showEmptySlots
      state.ui.showEmptySlots = next
      state.ui.showVirtualPosts = next ? state.ui.showVirtualPosts : false
    },
    toggleVirtualPostsVisible: (state) => {
      const next = !state.ui.showVirtualPosts
      state.ui.showVirtualPosts = next
      state.ui.showEmptySlots = next ? true : state.ui.showEmptySlots
    },
    setPlannedPostsView: (state, action: PayloadAction<PlannedPostsLayout>) => {
      state.ui.plannedPostsView = action.payload
    },
    setCalendarViewMode: (state, action: PayloadAction<'month' | 'week'>) => {
      state.ui.calendarMode = action.payload
    },
    setWeekViewExpanded: (state, action: PayloadAction<boolean>) => {
      state.ui.weekViewExpanded = action.payload
    },
    setCalendarPostSize: (state, action: PayloadAction<CalendarPostSize>) => {
      state.ui.calendarPostSize = action.payload
    },
    setCalendarMonthStart: (state, action: PayloadAction<CalendarMonthStartOption>) => {
      state.ui.calendarMonthStart = action.payload
    },
    setCalendarWeekStart: (state, action: PayloadAction<Weekday>) => {
      state.ui.calendarWeekStart = action.payload
    },
    setCalendarPostedVisible: (state, action: PayloadAction<boolean>) => {
      state.ui.showPostedInCalendar = action.payload
    },
    toggleSidebarProfilesExpanded: (state) => {
      state.ui.sidebarProfilesExpanded = !state.ui.sidebarProfilesExpanded
    }
  },
  extraReducers: builder => {
    builder
      .addCase(setComposerProfiles, (state, action) => {
        state.ui.selectedProfiles = action.payload
      })
      .addCase(startImpersonation, (state) => {
        const initial = initialState()
        state.autoPostSlots = initial.autoPostSlots
        state.ui = initial.ui
        state.buckets = initial.buckets
        state.bucketPresets = initial.bucketPresets
      })
      .addCase(createPostSuccess, (state) => {
        state.newPost = Date.now().toString()
      })
      .addCase(getPostSlotsSuccess, (state, action) => {
        const newSlots = action.payload.slots.sort(sortByKeyAscending('time'))
        const slots = action.payload.page === 0 ? newSlots : [...state.autoPostSlots, ...newSlots]
        state.autoPostSlots = slots
      })
      .addCase(getDestinationsSuccess, (state, action) => {
        // NOTE: If any of the currently selected profiles is not connected, reset selection to default profiles
        const currentSelectedProfileIds = state.ui.selectedProfiles.map(p => p.id)
        const connectedProfileIds: string[] = []
        const defaultProfiles: PostDestination[] = []
        let selectedProfilesConnected = true

        for (const p of action.payload) {
          if (p.connected) {
            connectedProfileIds.push(p.id)
            if (p.default) {
              defaultProfiles.push(p)
            }
          }
        }

        for (const id of currentSelectedProfileIds) {
          if (!connectedProfileIds.includes(id)) {
            selectedProfilesConnected = false
            break
          }
        }

        if (selectedProfilesConnected) {
          return
        }

        state.ui.selectedListId = undefined
        state.ui.selectedProfiles = defaultProfiles
      })
      .addCase(getBuckets.fulfilled, (state, action) => {
        // Do not override pages activeSlotsCount when undefined
        const existingBucketsMap = state.buckets.reduce((acc, bucket) => {
          acc[bucket.id] = bucket
          return acc
        }, {} as Record<string, typeof state.buckets[0]>)

        const buckets = action.payload.buckets.map(bucket => {
          const existing = existingBucketsMap[bucket.id]
          if (existing) {
            const pagesMap = existing.pages.reduce((acc, page) => {
              acc[page.ppid] = page
              return acc
            }, {} as Record<string, typeof existing.pages[0]>)

            const pages = bucket.pages.map(page => {
              const existingPage = pagesMap[page.ppid]
              if (existingPage) {
                return {
                  ...page,
                  slotsCount: page.slotsCount !== undefined ? page.slotsCount : existingPage.slotsCount
                }
              }
              return page
            })
            return { ...bucket, pages }
          }
          return bucket
        })
        state.buckets = buckets
        state.bucketGroups = action.payload.groups
      })
      .addCase(getBucketById.fulfilled, (state, action) => {
        const bucket = action.payload
        const index = state.buckets.findIndex(b => b.id === bucket.id)
        if (index === -1) {
          state.buckets = [...state.buckets, bucket]
        } else {
          state.buckets[index] = bucket
        }
      })
      .addCase(createBucket.fulfilled, (state, action) => {
        state.buckets = [...state.buckets, action.payload]
      })
      .addCase(updateBucket.fulfilled, (state, action) => {
        const bucket = action.payload.bucket
        const index = state.buckets.findIndex(b => b.id === bucket.id)
        if (index !== -1) {
          state.buckets.splice(index, 1, bucket)
        }
      })
      .addCase(deleteBucket.fulfilled, (state, action) => {
        state.buckets = state.buckets.filter(b => b.id !== action.payload)
      })
      .addCase(reorderBuckets.fulfilled, (state, action) => {
        const map = action.payload.reduce((acc: Record<string, number>, id: string, index: number) => {
          acc[id] = index
          return acc
        }, {})
        state.buckets.forEach(b => {
          const index = map[b.id]
          if (index !== undefined) {
            b.order = index
          }
        })
      })
      .addCase(getDefaultBuckets.fulfilled, (state, action) => {
        state.bucketPresets = action.payload
      })
      .addCase(getFailedPostsCount.pending, (state) => {
        state.failed.loading = true
      })
      .addCase(getFailedPostsCount.fulfilled, (state, action) => {
        state.failed = {
          ppids: action.payload.ppids,
          hasFailedPosts: action.payload.hasFailedPosts,
          loading: false
        }
      })
      .addCase(getFailedPostsCount.rejected, (state) => {
        state.failed = {
          hasFailedPosts: false,
          ppids: [],
          loading: false
        }
      })
      .addCase(getPostsCount.fulfilled, (state, action) => {
        const countByPage = (action.payload as any)?.planned?.countByPage as Array<{ ppPageId: string, count: number }> | undefined
        if (!countByPage) {
          return
        }
        state.postsCount = countByPage.reduce((acc: Record<string, number>, { ppPageId, count }) => {
          acc[ppPageId] = count
          return acc
        }, {})
      })
      .addCase(setSelectedProfiles, (state, action) => {
        const selectedProfilesNext = action.payload.profiles
        const plannedPostsView = state.ui.plannedPostsView

        state.ui.selectedProfiles = selectedProfilesNext
        state.ui.plannedPostsView = plannedPostsView
      })
      .addCase(updateBucketGroup.fulfilled, (state, action) => {
        const groupIndex = state.bucketGroups.findIndex(g => g.id === action.payload.id)
        if (groupIndex !== -1) {
          state.bucketGroups[groupIndex].name = action.payload.name
        }
      })
      .addCase(deleteBucketGroup.fulfilled, (state, action) => {
        const id = action.payload
        state.bucketGroups = state.bucketGroups.filter(g => g.id !== id)
      })
      .addCase(logoutAction, () => initialState())
  }
})

export const {
  setSelectedList,
  setActiveSidebarView,
  toggleEmptySlotsVisible,
  toggleVirtualPostsVisible,
  setPlannedPostsView,
  setCalendarViewMode,
  setWeekViewExpanded,
  setCalendarPostSize,
  setCalendarMonthStart,
  setCalendarWeekStart,
  setCalendarPostedVisible,
  toggleSidebarProfilesExpanded
} = postsSlice.actions
export { setSelectedProfiles }
export default postsSlice.reducer
